import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from '@/composables/useI18n';
import { useTableQuery } from '@/composables/useTableQuery';
import { useStore } from '@/store';
import { createReportMetricsApi } from '@/views/reports/report-metrics-api';
import { buildBaseMetricsBundle } from '@/views/reports/pickers/base/base-metrics-bundle';
import { getStaticMetricsBundles } from '@/views/reports/report-metrics-bundles-static';
import {
  createMetricsPickerContext,
  type MetricsPickerDedicatedBundles,
} from '@/views/reports/pickers/base/metrics-picker-context';
import { resolveReportTypeFromRoute } from '@/views/reports/report-route-behavior';

export const useMetricsPickerContext = () => {
  const route = useRoute();
  const { t } = useI18n();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const groupingValues = ref<unknown[]>([]);
  const getDedicatedMetricsBundles = (): MetricsPickerDedicatedBundles => {
    const staticBundles = getStaticMetricsBundles();

    return {
      base: buildBaseMetricsBundle((key) => t(key)),
      conversions: staticBundles.conversions,
      postbacks: staticBundles.postbacks,
      errors: staticBundles.errors,
      'live-clicks': staticBundles['live-clicks'],
      'live-visits': staticBundles['live-visits'],
    };
  };

  const reportMetricsStore = {
    getCustomEvents: () => tableQueryApi.getTableCustomEvents(),
    getCustomColumns: () => store.getCustomColumns(),
    getDimensions: () =>
      store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>,
    getMetrics: () => store.metrics as string[],
    getIsHideFilters: () => tableQueryApi.getTableIsHideFilters(),
    getIndependentColumnSettingsEnabled: () => store.user.independentColumnSettingsEnabled,
  };

  const reportMetricsApi = createReportMetricsApi({
    translate: (name) => t(name),
    store: reportMetricsStore,
    getTableQueryForCurrentTab: () => tableQueryApi.getTableQueryForCurrentTab(),
    getDedicatedMetricsBundles,
  });

  const reportMetricsName = (key: string) => reportMetricsApi.reportMetricsName(key);

  const metricsPicker = createMetricsPickerContext({
    translate: (key) => t(key),
    store: reportMetricsStore,
    getTableQueryForCurrentTab: () => tableQueryApi.getTableQueryForCurrentTab(),
    getRouteName: () => route.name,
    getRouteType: () => route.params.type as string,
    getDedicatedMetricsBundles,
    reportMetricsName,
    getGroupingValues: () => groupingValues.value,
    setGroupingValues: (values) => {
      groupingValues.value = values;
    },
  });

  const groupingValuesValueList = computed(() => {
    return groupingValues.value.map((item: { value?: string }) => item.value);
  });

  const routeName = computed(() => route.name);

  const storeColumns = computed(() => {
    return resolveReportTypeFromRoute(route.name, route.params.type as string);
  });

  const existingMetrics = computed(() => metricsPicker.getExistingMetrics());

  const baseMetrics = computed(() => metricsPicker.getBaseMetrics());

  return {
    route,
    t,
    groupingValues,
    groupingValuesValueList,
    routeName,
    storeColumns,
    reportMetricsName,
    metricsPicker,
    existingMetrics,
    baseMetrics,
    getInheritColumns: metricsPicker.getInheritColumns,
    findOrderBy: metricsPicker.findOrderBy,
    dimensionType: metricsPicker.dimensionType,
    getDefaultSelected: metricsPicker.getDefaultSelected,
    tableFullDimensions: metricsPicker.tableFullDimensions,
    getMetrics: metricsPicker.getMetrics,
    existingMetricsCustom: metricsPicker.existingMetricsCustom,
    getExistingMetricsSimple: metricsPicker.getExistingMetricsSimple,
    getCustomMetrics: metricsPicker.getCustomMetrics,
  };
};
